import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight, Phone, Star, MapPin } from "lucide-react";
import { Reveal, stagger, item } from "../components/Reveal";
import { Counter } from "../components/Counter";
import { IMG, STATS, WHY, SERVICES, PROCESS, PROJECTS, BRAND_LOGOS, TESTIMONIALS, SUBURBS } from "../data/site";

const heroLines = ["Melbourne's Trusted", "Heating & Cooling", "Specialists Since 1988"];

const Icon = ({ name, ...p }) => { const C = Icons[name] || Icons.Snowflake; return <C {...p} />; };

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden bg-js-charcoal">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={IMG.hero} alt="Luxury Melbourne home interior" className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/35 to-black/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pb-20 pt-32 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs font-medium tracking-wide">
            <span className="w-2 h-2 rounded-full bg-js-red animate-pulse" /> 35+ years · Residential & Commercial · Licensed
          </motion.div>
          <h1 data-testid="hero-heading" className="font-display font-extrabold text-white text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tighter max-w-5xl">
            {heroLines.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  initial={{ y: "110%" }} animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
            className="mt-6 text-white/80 text-lg max-w-xl leading-relaxed">
            Over 35 years delivering premium heating, cooling and ventilation solutions across Melbourne.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }}
            className="mt-9 flex flex-wrap gap-4">
            <Link to="/quote" data-testid="hero-quote-btn" className="group flex items-center gap-2 px-7 py-4 rounded-full bg-js-blue text-white font-semibold hover:bg-js-dark transition-colors duration-300">
              Request Free Quote <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <a href="tel:+61393303911" data-testid="hero-call-btn" className="flex items-center gap-2 px-7 py-4 rounded-full bg-white/10 backdrop-blur-md border border-white/25 text-white font-semibold hover:bg-white/20 transition-colors duration-300">
              <Phone size={18} /> Call Now
            </a>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/60">
          <span className="text-[10px] tracking-[0.25em]">SCROLL</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="w-px h-8 bg-white/40" />
        </motion.div>
      </section>

      {/* BRAND MARQUEE */}
      <section className="py-10 bg-white border-b border-black/5">
        <p className="text-center text-xs tracking-[0.25em] text-js-charcoal/40 mb-6">AUTHORISED INSTALLERS OF THE WORLD'S BEST</p>
        <div className="marquee-fade">
          <Marquee speed={38} gradient={false} pauseOnHover>
            {[...BRAND_LOGOS, ...BRAND_LOGOS, ...BRAND_LOGOS, ...BRAND_LOGOS].map((b, i) => (
              <span key={i} className="mx-10 inline-flex items-center h-14 align-middle">
                <img src={b.logo} alt={b.name} className="h-8 md:h-9 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity duration-300" />
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* WHY CHOOSE — BENTO */}
      <section className="py-24 md:py-32 bg-js-grey">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <span className="text-js-blue font-semibold text-sm tracking-wide">WHY CHOOSE J&amp;S</span>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-3 text-balance">Trusted heating &amp; cooling solutions across Melbourne</h2>
          </Reveal>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid gap-5 md:grid-cols-3">
            {WHY.map((w, i) => (
              <motion.div key={w.title} variants={item}
                className={`group bg-white rounded-3xl p-8 border border-black/5 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(11,78,162,0.12)] transition-[transform,box-shadow] duration-400 ${i === 0 ? "md:col-span-2" : ""}`}>
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-js-blue/10 text-js-blue mb-5 group-hover:bg-js-blue group-hover:text-white transition-colors duration-300">
                  <Icon name={w.icon} size={22} />
                </span>
                <h3 className="font-display font-bold text-xl mb-2">{w.title}</h3>
                <p className="text-js-charcoal/60 leading-relaxed">{w.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <span className="text-js-blue font-semibold text-sm tracking-wide">WHAT WE DO</span>
              <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-3">Premium heating & cooling, done right</h2>
            </div>
            <Link to="/services" className="flex items-center gap-2 text-js-blue font-semibold hover:gap-3 transition-all duration-300">View all services <ArrowRight size={18} /></Link>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.05}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block relative overflow-hidden rounded-3xl h-72 border border-black/5">
                  <img src={s.image} alt={s.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-js-charcoal/90 via-js-charcoal/30 to-transparent" />
                  <div className="absolute inset-0 p-7 flex flex-col justify-end text-white">
                    <span className="grid place-items-center w-11 h-11 rounded-xl bg-white/15 backdrop-blur-md border border-white/20 mb-3 w-fit"><Icon name={s.icon} size={20} /></span>
                    <h3 className="font-display font-bold text-xl flex items-center gap-2">{s.name} <ArrowUpRight size={18} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 -translate-x-1 transition-all duration-300" /></h3>
                    <p className="text-white/75 text-sm mt-1">{s.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 md:py-32 bg-js-charcoal text-white relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
          <Reveal className="max-w-2xl mb-16">
            <span className="text-js-red font-semibold text-sm tracking-wide">HOW IT WORKS</span>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-3">Four simple steps to total comfort</h2>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1}>
                <div className="relative">
                  <span className="font-display font-extrabold text-6xl text-white/10">{p.n}</span>
                  <div className="h-px w-full bg-white/10 my-5 relative">
                    <span className="absolute left-0 top-0 h-px w-12 bg-js-red" />
                  </div>
                  <h3 className="font-display font-bold text-xl mb-2">{p.title}</h3>
                  <p className="text-white/60 leading-relaxed text-sm">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <span className="text-js-blue font-semibold text-sm tracking-wide">OUR WORK</span>
              <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-3">Featured projects across Melbourne</h2>
            </div>
            <Link to="/projects" className="flex items-center gap-2 text-js-blue font-semibold hover:gap-3 transition-all duration-300">All projects <ArrowRight size={18} /></Link>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {PROJECTS.slice(0, 6).map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div data-testid="project-card" className="group relative overflow-hidden rounded-3xl h-80 border border-black/5">
                  <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-js-charcoal/95 via-js-charcoal/20 to-transparent opacity-90" />
                  <div className="absolute inset-0 p-7 flex flex-col justify-end translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    <span className="text-js-red text-xs font-semibold tracking-wide mb-1">{p.category}</span>
                    <h3 className="font-display font-bold text-white text-lg">{p.title}</h3>
                    <div className="max-h-0 group-hover:max-h-40 overflow-hidden transition-all duration-500">
                      <p className="text-white/70 text-sm mt-2 flex items-center gap-1.5"><MapPin size={13} /> {p.location}</p>
                      <p className="text-white/70 text-sm">{p.system}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-24 bg-js-blue text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s) => (
            <Reveal key={s.label} className="text-center">
              <div className="font-display font-extrabold text-5xl md:text-6xl tracking-tight" data-testid={`stat-${s.label}`}>
                <Counter value={s.value} suffix={s.suffix} />
              </div>
              <p className="text-white/70 mt-2 text-sm md:text-base">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-js-grey">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <span className="text-js-blue font-semibold text-sm tracking-wide">REVIEWS</span>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-3">Loved by Melbourne homes & businesses</h2>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.05}>
                <div className="bg-white rounded-3xl p-8 border border-black/5 shadow-[0_8px_32px_rgba(0,0,0,0.04)] h-full">
                  <div className="flex gap-0.5 mb-4">{Array.from({ length: t.rating }).map((_, k) => <Star key={k} size={16} className="fill-js-red text-js-red" />)}</div>
                  <p className="text-js-charcoal/80 leading-relaxed mb-6">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-js-blue/10 text-js-blue font-display font-bold">{t.name[0]}</span>
                    <div><p className="font-semibold text-sm">{t.name}</p><p className="text-js-charcoal/50 text-xs">{t.suburb}</p></div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="text-js-blue font-semibold text-sm tracking-wide">SERVICE AREAS</span>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-3 mb-6">Proudly servicing Melbourne & regional Victoria</h2>
            <p className="text-js-charcoal/60 leading-relaxed mb-8">From Melbourne Metro to the Peninsulas and regional centres, our licensed team delivers premium heating, cooling and ventilation across Victoria.</p>
            <div className="flex flex-wrap gap-2.5">
              {SUBURBS.map((s) => (
                <span key={s} className="px-4 py-2 rounded-full bg-js-grey border border-black/5 text-sm font-medium text-js-charcoal/70 flex items-center gap-1.5"><MapPin size={13} className="text-js-blue" />{s}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative rounded-3xl overflow-hidden h-[420px] border border-black/5 shadow-[0_20px_50px_rgba(0,0,0,0.08)]">
              <img src={IMG.melbourne} alt="Melbourne" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-js-blue/20" />
              <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-md rounded-2xl px-5 py-4">
                <p className="font-display font-bold text-js-charcoal">Based in Tullamarine</p>
                <p className="text-sm text-js-charcoal/60">Servicing Melbourne & regional Victoria</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <CtaBanner />
    </div>
  );
};

export const CtaBanner = () => (
  <section className="bg-js-charcoal py-20 md:py-28">
    <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
      <Reveal>
        <h2 className="font-display font-extrabold text-white text-4xl md:text-6xl tracking-tight text-balance">Need Heating or Cooling?</h2>
        <p className="text-white/60 text-lg mt-4">Get a free, no-obligation quote today.</p>
        <div className="mt-9 flex flex-wrap justify-center gap-4">
          <Link to="/quote" data-testid="cta-quote-btn" className="group flex items-center gap-2 px-8 py-4 rounded-full bg-js-red text-white font-semibold hover:bg-js-red/90 transition-colors duration-300">
            Get a Free Quote <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          <a href="tel:+61393303911" className="flex items-center gap-2 px-8 py-4 rounded-full bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/20 transition-colors duration-300"><Phone size={18} /> Call Now</a>
        </div>
        <nav aria-label="Quick links" className="mt-9 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/55">
          <Link to="/services" className="hover:text-white transition-colors duration-300">Air Conditioning Services</Link>
          <Link to="/projects" className="hover:text-white transition-colors duration-300">Our HVAC Projects</Link>
          <Link to="/about" className="hover:text-white transition-colors duration-300">About J&amp;S Air Conditioning</Link>
          <Link to="/careers" className="hover:text-white transition-colors duration-300">Careers at J&amp;S</Link>
          <Link to="/contact" className="hover:text-white transition-colors duration-300">Contact J&amp;S Air Conditioning</Link>
        </nav>
      </Reveal>
    </div>
  </section>
);

export default Home;
